<template>
    <div class="mx-auto">
        <!-- Page Header -->
        <div class="page-header">
            <PageBreadcrumb>
                <template #actions>
                    <small
                        v-can-access.permissions="$Permissions.READ_SERVICES"
                        v-if="!loaderStore.isLoading('services.fetch') && servicesStore.current"
                        class="inline-flex items-center justify-center">
                        <Badge variant="secondary"> <b>Service:</b> {{ servicesStore.current?.name }} </Badge>
                    </small>
                    <SkeletonLoader
                        v-can-access.permissions="$Permissions.READ_SERVICES"
                        preset="badge"
                        :loading="loaderStore.isLoading('services.fetch') || !servicesStore.current" />
                </template>
            </PageBreadcrumb>
        </div>

        <!-- Toolbar: Search and Create -->
        <div class="flex justify-between items-center mb-6">
            <div class="flex gap-3 items-center">
                <Button
                    v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                    variant="outline"
                    class="gap-2"
                    :disabled="loaderStore.isLoading('kafkaUsers.fetch')"
                    @click="openAddModal">
                    <PlusCircle class="h-4 w-4" />
                    Create User
                </Button>
                <SearchInput
                    :loading="loaderStore.isLoading('kafkaUsers.fetch')"
                    placeholder="Search by username or IP"
                    @search="(input) => (searchQuery = input)" />
            </div>
        </div>

        <!-- Data Table -->
        <ScrollArea
            class="w-full border shadow-md rounded-md"
            :class="
                (loaderStore.isLoading('kafkaUsers.fetch') && 5 <= 10) || (!loaderStore.isLoading('kafkaUsers.fetch') && (filteredUsers?.length || 0) <= 10)
                    ? 'h-auto'
                    : 'h-70-dynamic'
            ">
            <Table>
                <TableHeader class="sticky top-0 bg-background z-10">
                    <TableRow class="bg-muted/50">
                        <TableHead class="first:rounded-tl-md">Username</TableHead>
                        <TableHead>Whitelisted IPs</TableHead>
                        <TableHead>Proxy Endpoint</TableHead>
                        <TableHead>ACLs</TableHead>
                        <TableHead class="last:rounded-tr-md"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <!-- Skeleton rows when loading -->
                    <template v-if="loaderStore.isLoading('kafkaUsers.fetch')">
                        <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="ip-address" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="endpoint" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                        </TableRow>
                    </template>
                    <!-- Actual data rows -->
                    <template v-else>
                        <template v-if="filteredUsers.length">
                            <template v-for="user in filteredUsers" :key="user.username">
                                <TableRow>
                                    <TableCell class="font-medium">
                                        <div class="flex items-center gap-2">
                                            <User class="h-5 w-5 text-muted-foreground" />
                                            <span>{{ user.username }}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex flex-wrap gap-1">
                                            <template
                                                v-for="(ip, idx) in user.whitelist_ips
                                                    ? user.whitelist_ips
                                                          .split(',')
                                                          .map((v) => v.trim())
                                                          .filter((v) => v)
                                                    : []"
                                                :key="idx">
                                                <Badge variant="outline" class="text-xs">
                                                    {{ ip }}
                                                </Badge>
                                            </template>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex flex-col gap-1">
                                            <template
                                                v-for="(ep, idx) in user.kafka_proxy_endpoint
                                                    ? user.kafka_proxy_endpoint
                                                          .split(',')
                                                          .map((v) => v.trim())
                                                          .filter((v) => v)
                                                    : []"
                                                :key="idx">
                                                <span class="text-xs text-muted-foreground">{{ ep }}</span>
                                            </template>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex flex-wrap gap-1">
                                            <template v-for="(acl, idx) in user.kafka_acls.slice(0, 3)" :key="idx">
                                                <Badge variant="outline" class="text-xs">
                                                    {{ acl.name }} ({{ acl.operation.toUpperCase() }}, {{ acl.resource_pattern_type.toUpperCase() }} /
                                                    {{ acl.resource.toUpperCase() }})
                                                </Badge>
                                            </template>
                                            <Button
                                                v-if="user.kafka_acls.length > 3"
                                                variant="link"
                                                class="h-auto p-0 text-xs text-primary hover:underline ml-1"
                                                @click="toggleExpand(user.username)">
                                                {{ expandedUser === user.username ? 'Hide' : 'View All' }}
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell class="text-right">
                                        <div class="flex items-center justify-end gap-2">
                                            <Button
                                                v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                                                variant="ghost"
                                                size="icon"
                                                class="h-8 w-8"
                                                @click="openEditModal(user)">
                                                <Edit class="h-4 w-4" />
                                            </Button>
                                            <Button
                                                v-can-access.permissions="[$Permissions.WRITE_KAFKA_ACCESS]"
                                                variant="ghost"
                                                size="icon"
                                                class="h-8 w-8 text-destructive hover:text-destructive"
                                                @click="handleDeleteUser(user)">
                                                <Trash class="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                <TableRow v-if="expandedUser === user.username">
                                    <TableCell colspan="5" class="bg-muted/50">
                                        <ul class="list-disc list-inside text-sm text-muted-foreground">
                                            <li v-for="(acl, idx) in user.kafka_acls" :key="idx">
                                                {{ acl.name }} - {{ acl.operation.toUpperCase() }} ({{ acl.resource_pattern_type.toUpperCase() }},
                                                {{ acl.resource.toUpperCase() }})
                                            </li>
                                        </ul>
                                    </TableCell>
                                </TableRow>
                            </template>
                        </template>
                        <TableEmpty v-if="!loaderStore.isLoading('kafkaUsers.fetch') && !filteredUsers.length" :colspan="5">
                            <TableEmptyState />
                        </TableEmpty>
                    </template>
                </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
            <ScrollBar orientation="vertical" />
        </ScrollArea>

        <!-- Add/Edit Modal -->
        <KafkaUserAddEditModal :is-open="isModalOpen" :user="selectedUser" @update:isOpen="(val) => (isModalOpen = val)" @save="handleSaveUser" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import PageBreadcrumb from '@/components/PageBreadcrumb.vue';
import SearchInput from '@/components/global/SearchInput.vue';
import AppLoader from '@/components/AppLoader.vue';
import { PlusCircle, Edit, Trash, User } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useKafkaUsersStore } from '@/stores/kafkaUsers.store';
import { useLoaderStore } from '@/stores/loader.store';
import { useServicesStore } from '@/stores/services.store';
import { Permissions } from '@/utils/constants';
import { useDeleteConfirmation } from '@/composables/useDialog';
import KafkaUserAddEditModal from '@/components/kafkaAccess/KafkaUserAddEditModal.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Badge } from '@/components/ui/badge';
import { TableEmptyState } from '@/components/ui/table-empty-state';

const kafkaUsersStore = useKafkaUsersStore();
const loaderStore = useLoaderStore();
const servicesStore = useServicesStore();

const searchQuery = ref('');
const isModalOpen = ref(false);
const selectedUser = ref(null);
const expandedUser = ref(null);

const filteredUsers = computed(() => {
    const q = searchQuery.value.toLowerCase();
    return kafkaUsersStore.all.filter((u) => u.username.toLowerCase().includes(q) || u.whitelist_ips.toLowerCase().includes(q));
});

onMounted(() => {
    kafkaUsersStore.fetch();
});

const openAddModal = () => {
    selectedUser.value = null;
    isModalOpen.value = true;
};

const openEditModal = (user) => {
    selectedUser.value = user;
    isModalOpen.value = true;
};

const handleSaveUser = async (payload) => {
    try {
        if (selectedUser.value) {
            await kafkaUsersStore.updateUser(selectedUser.value.username, payload);
        } else {
            await kafkaUsersStore.addUser(payload);
        }
        // Only close modal on successful save
        isModalOpen.value = false;
    } catch (error) {
        console.error('Error saving user:', error);
        // Keep modal open on error - user can retry or cancel manually
    }
};

const handleDeleteUser = async (user) => {
    try {
        await useDeleteConfirmation({
            entity: 'Kafka user',
            name: user.username,
            title: 'Delete Kafka User',
            message: `Are you sure you want to delete the Kafka user <strong>"${user.username}"</strong>? This action cannot be undone.`,
            onConfirm: async () => {
                await kafkaUsersStore.deleteUser(user.username);
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    }
};

const toggleExpand = (username) => {
    expandedUser.value = expandedUser.value === username ? null : username;
};
</script>

<style scoped>
/* Add component-specific styles if needed */
</style>
